import { Box, Link, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import numeral from 'numeral';
import React, { useCallback, useRef, useState } from 'react';
import { contentTypeIcon } from '../../../../components/StoryItem';
import useLazyload from '../../../../hooks/useLazyload';
import { ICollection, ICollectionDisplayType, IStoryOrSeasonId, StoryType } from '../../../../services/types';
import { getMatchedSeason } from '../../../../services/utils';
import CurationInfo from './CurationInfo';

const useStyles = makeStyles((theme: Theme) => ({
  coverUrl: {
    objectFit: 'cover',
    objectPosition: 'top',
    borderRadius: 5,
    marginBottom: 1,
    width: '140px',
    height: '140px',
  },
  label: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    position: 'absolute',
    left: 0,
    bottom: 6,
    backgroundColor: '#000',
    color: '#fff',
    padding: '0 8px',
    fontFamily: 'Georgia, serif',
    fontWeight: 'bold',
    fontSize: 12,
  },
  collectionTitle: {
    paddingLeft: '4px',
    display: 'flex',
  },
  rank: {
    fontFamily: 'Georgia, serif',
    fontSize: 18,
    fontWeight: 'bold',
  },
  title: {
    lineHeight: 1.1,
    fontSize: 14,
    fontWeight: 500,
  },
  themeText: {
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
    overflow: 'hidden',
    fontSize: 12,
    color: theme.palette.primary.main,
    fontFamily: 'Georgia, serif',
  },
  views: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  internalTitle: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2),
  },
  icon: {
    width: 48,
    height: 48,
    borderRadius: 5,
    objectFit: 'cover',
    marginLeft: 4,
  },
  discountRate: {
    position: 'absolute',
    left: 4,
    bottom: 0,
    backgroundColor: '#000',
    padding: '2px 4px',
    color: '#4cd964',
    fontSize: 10,
    fontWeight: 500,
  },
}));

interface IProps {
  curation?: { id: number; segmentId: number; priority: number };
  collection: ICollection;
  targets?: IStoryOrSeasonId[];
}

const DefaultImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNM+w8AAdEBZxIy2ZkAAAAASUVORK5CYII=';
const regex = /(http[s]?:\/\/|data:image)/;

const PreviewCollection = (props: IProps): JSX.Element | null => {
  const classes = useStyles();

  const { collection, curation, targets } = props;
  const { title, internalTitle, stories, showRanking, subTitle, iconUrl, displayType, discountRate, subBackgroundColor } = collection;

  if (!stories || stories.length === 0) {
    return null;
  }

  return (
    <Box marginBottom={2} bgcolor={displayType === ICollectionDisplayType.Sale ? subBackgroundColor : undefined}>
      <Box display="flex" alignItems="center">
        {iconUrl && <img src={iconUrl} className={classes.icon} />}
        <Typography variant="subtitle1" className={classes.collectionTitle}>
          <Box>
            <Box>{title}</Box>
            {subTitle && <Box>{subTitle}</Box>}
          </Box>
          {internalTitle && <span className={classes.internalTitle}>{`(${internalTitle})`}</span>}
          {curation && <CurationInfo location="inline" segmentId={curation.segmentId} priority={curation.priority} />}
        </Typography>
      </Box>

      <Box width="100%" overflow="auto" paddingTop={1}>
        <Box display="flex" flexDirection="row" padding="2px 0" width="max-content">
          {stories?.map((s, i) => {
            let season = null;
            let seasonId = null;

            if (targets && targets[i].seasonId) {
              seasonId = targets[i].seasonId;
              s.seasonId = seasonId || undefined;
            }
            season = getMatchedSeason(s);

            return (
              <>
                <Story
                  storyId={s.id}
                  seasonId={s.seasonId}
                  key={`preview-collection-${i}-${s.id}-${s.seasonId ? s.seasonId : 'story'}`}
                  coverUrl={season ? season!.coverUrl : null}
                  title={seasonId ? season!.title : s.title}
                  themeText={s.themeText}
                  views={seasonId ? season!.viewCount : s.viewCount}
                  ranking={showRanking ? i + 1 : null}
                  discountRate={discountRate}
                  storyType={s.type}
                />
              </>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

interface IPreviewStoryProps {
  storyId: number;
  seasonId: number | undefined | null;
  coverUrl: string | null;
  title: string;
  themeText?: string;
  views: number;
  ranking: number | null;
  discountRate?: number;
  storyType: StoryType;
}

const Story = (props: IPreviewStoryProps) => {
  const classes = useStyles();
  const { coverUrl, title, themeText, views, ranking, storyId, seasonId, discountRate, storyType } = props;

  const imgRef = useRef<HTMLImageElement | null>();
  const [isShowImage, setIsShowImage] = useState<boolean>(false);

  const imgSrc = coverUrl ? (regex.test(coverUrl) ? `${coverUrl}?w=240` : `${process.env.CDN_URL}/${coverUrl}?w=240`) : undefined;
  const handleImageLoad = useCallback(() => setIsShowImage(true), []);

  useLazyload(imgRef, handleImageLoad);

  return (
    <Box padding={0.5} width={'148px'} overflow="hidden" lineHeight={1.1}>
      <Link href={`${process.env.CREW_DEFAULT_URI}/stories/${storyId}${seasonId ? `/seasons/${seasonId}` : ''}`} target="_blank">
        <Box position="relative">
          <Box position={'absolute'}>
            <StoryTypeIcon storyType={storyType} />
          </Box>
          {imgSrc && <img src={isShowImage ? imgSrc : DefaultImage} className={classes.coverUrl} ref={ref => (imgRef.current = ref)} alt={title} />}
          {!imgSrc && <img src={DefaultImage} className={classes.coverUrl} width={240} height={240} alt={title} />}
          {discountRate !== undefined ? <Box className={classes.discountRate}>{`${discountRate}% OFF`}</Box> : undefined}
        </Box>

        {ranking && <Typography className={classes.rank}>{ranking}</Typography>}
        <Typography className={classes.themeText}>{themeText}</Typography>
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.views}>{`${numeral(views).format('0.[0]a')} views`}</Typography>
      </Link>
    </Box>
  );
};
interface IStoryTypeIconsProps {
  storyType: StoryType;
}

const StoryTypeIcon = (props: IStoryTypeIconsProps) => {
  const { storyType } = props;
  return storyType === StoryType.Audio || storyType === StoryType.WebComic
    ? contentTypeIcon(storyType, {
        color: '#ffffff',
        width: 24,
        height: 24,
        style: {
          backgroundColor: '#000000',
          marginTop: 4,
          marginLeft: 4,
          borderRadius: 4,
          padding: '4px 0px 3px 4px',
        },
      })
    : null;
};

export default PreviewCollection;
